import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { FieldAccessResult } from '../types/fieldPermissions';

interface FieldPermissionCache {
  [key: string]: FieldAccessResult;
}

interface UseFieldPermissionsOptions {
  preloadSections?: string[];
  enableCaching?: boolean;
  autoRefresh?: boolean;
  requestStage?: string; // Optional stage for stage-based permissions
  requestId?: string; // Optional request ID for partner contributor access level
}

/**
 * Hook for managing field-level permissions with caching and preloading
 */
export const useFieldPermissions = (options: UseFieldPermissionsOptions = {}) => {
  const { user } = useAuth();
  const { preloadSections = [], enableCaching = true, autoRefresh = true, requestStage, requestId } = options;

  const [permissionCache, setPermissionCache] = useState<FieldPermissionCache>({});
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const hasPreloadedRef = useRef(false);

  // Generate cache key for field permission
  const getCacheKey = useCallback((section: string, field: string, stage?: string, reqId?: string): string => {
    const baseKey = stage ? `${stage}:${section}:${field}` : `${section}:${field}`;
    // Include requestId in cache key for partner contributors who have per-request access levels
    return reqId ? `${baseKey}:${reqId}` : baseKey;
  }, []);

  // Preload permissions for specified sections
  const preloadPermissions = useCallback(async () => {
    if (!user?.roles || preloadSections.length === 0) {
      setIsInitialized(true);
      return;
    }

    const newCache: FieldPermissionCache = {};

    try {
      for (const section of preloadSections) {
        const accessibleFields = await fieldPermissionService.getAccessibleFields(
          user.roles,
          section,
          requestStage,
          requestId,
          user.email,
        );

        for (const { fieldName, access } of accessibleFields) {
          const cacheKey = getCacheKey(section, fieldName, requestStage, requestId);
          newCache[cacheKey] = access;
        }
      }

      if (enableCaching) {
        setPermissionCache(newCache);
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to preload field permissions:', error);
      setIsInitialized(true);
    }
  }, [user?.roles, user?.email, preloadSections, enableCaching, getCacheKey, requestStage, requestId]);

  // Initialize permissions - only run once when user is available
  useEffect(() => {
    // Only preload once
    if (autoRefresh && user?.roles && !hasPreloadedRef.current) {
      hasPreloadedRef.current = true;
      preloadPermissions();
    } else {
      // Mark as initialized even if we're not preloading
      if (!autoRefresh || hasPreloadedRef.current) {
        setIsInitialized(true);
      }
    }
  }, [user?.roles, autoRefresh, preloadPermissions]);

  // Clear cache and reload when requestStage or requestId changes
  useEffect(() => {
    if ((requestStage || requestId) && hasPreloadedRef.current) {
      setPermissionCache({});
      setLoadingStates({});
      setIsInitialized(false);
      preloadPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preloadPermissions excluded to prevent infinite loop (it depends on requestStage/requestId)
  }, [requestStage, requestId]);

  // Get field permission with caching
  const getFieldPermission = useCallback(async (
    section: string,
    field: string,
    forceRefresh = false,
    overrideStage?: string
  ): Promise<FieldAccessResult> => {
    if (!user?.roles) {
      return { canRead: false, canWrite: false, isHidden: true };
    }

    const effectiveStage = overrideStage || requestStage;
    const cacheKey = getCacheKey(section, field, effectiveStage, requestId);

    // Return cached result if available and not forcing refresh
    if (!forceRefresh && enableCaching && permissionCache[cacheKey]) {
      return permissionCache[cacheKey];
    }

    // Set loading state
    setLoadingStates(prev => ({ ...prev, [cacheKey]: true }));

    try {
      const access = await fieldPermissionService.checkFieldAccess({
        userRoles: user.roles,
        section,
        field,
        requestStage: effectiveStage,
        requestId: requestId,
        userEmail: user.email,
      });

      // Update cache
      if (enableCaching) {
        setPermissionCache(prev => ({ ...prev, [cacheKey]: access }));
      }

      return access;
    } catch (error) {
      console.error(`Failed to get field permission for ${section}.${field}:`, error);
      return { canRead: false, canWrite: false, isHidden: true };
    } finally {
      // Clear loading state
      setLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[cacheKey];
        return newState;
      });
    }
  }, [user?.roles, getCacheKey, enableCaching, permissionCache, requestStage, requestId]);

  // Synchronous permission check (uses cache)
  const getFieldPermissionSync = useCallback((
    section: string,
    field: string,
    overrideStage?: string
  ): FieldAccessResult | null => {
    if (!enableCaching) return null;

    const effectiveStage = overrideStage || requestStage;
    const cacheKey = getCacheKey(section, field, effectiveStage, requestId);
    return permissionCache[cacheKey] || null;
  }, [enableCaching, getCacheKey, permissionCache, requestStage, requestId]);

  // Check if field is loading
  const isFieldLoading = useCallback((section: string, field: string, overrideStage?: string): boolean => {
    const effectiveStage = overrideStage || requestStage;
    const cacheKey = getCacheKey(section, field, effectiveStage, requestId);
    return Boolean(loadingStates[cacheKey]);
  }, [getCacheKey, loadingStates, requestStage, requestId]);

  // Convenience functions
  const canReadField = useCallback(async (section: string, field: string, overrideStage?: string): Promise<boolean> => {
    const access = await getFieldPermission(section, field, false, overrideStage);
    return access.canRead;
  }, [getFieldPermission]);

  const canWriteField = useCallback(async (section: string, field: string, overrideStage?: string): Promise<boolean> => {
    const access = await getFieldPermission(section, field, false, overrideStage);
    return access.canWrite;
  }, [getFieldPermission]);

  const isFieldHidden = useCallback(async (section: string, field: string, overrideStage?: string): Promise<boolean> => {
    const access = await getFieldPermission(section, field, false, overrideStage);
    return access.isHidden;
  }, [getFieldPermission]);

  // Synchronous convenience functions (use cache)
  const canReadFieldSync = useCallback((section: string, field: string, overrideStage?: string): boolean | null => {
    const access = getFieldPermissionSync(section, field, overrideStage);
    return access ? access.canRead : null;
  }, [getFieldPermissionSync]);

  const canWriteFieldSync = useCallback((section: string, field: string, overrideStage?: string): boolean | null => {
    const access = getFieldPermissionSync(section, field, overrideStage);
    return access ? access.canWrite : null;
  }, [getFieldPermissionSync]);

  const isFieldHiddenSync = useCallback((section: string, field: string, overrideStage?: string): boolean | null => {
    const access = getFieldPermissionSync(section, field, overrideStage);
    return access ? access.isHidden : null;
  }, [getFieldPermissionSync]);

  // Clear cache
  const clearCache = useCallback(() => {
    setPermissionCache({});
    setLoadingStates({});
    setIsInitialized(false);
    hasPreloadedRef.current = false;
  }, []);

  // Refresh all cached permissions
  const refreshCache = useCallback(async () => {
    if (!enableCaching) return;

    const cachedKeys = Object.keys(permissionCache);
    const newCache: FieldPermissionCache = {};

    for (const cacheKey of cachedKeys) {
      const [section, field] = cacheKey.split(':');
      if (section && field) {
        const access = await getFieldPermission(section, field, true);
        newCache[cacheKey] = access;
      }
    }

    setPermissionCache(newCache);
  }, [enableCaching, permissionCache, getFieldPermission]);

  // Memoized return object
  return useMemo(() => ({
    // Async functions
    getFieldPermission,
    canReadField,
    canWriteField,
    isFieldHidden,

    // Sync functions (cache-based)
    getFieldPermissionSync,
    canReadFieldSync,
    canWriteFieldSync,
    isFieldHiddenSync,

    // State
    isInitialized,
    isFieldLoading,

    // Cache management
    clearCache,
    refreshCache,
    preloadPermissions,

    // Utilities
    userRoles: user?.roles || [],
    cacheSize: Object.keys(permissionCache).length,
    hasCache: enableCaching,
  }), [
    getFieldPermission,
    canReadField,
    canWriteField,
    isFieldHidden,
    getFieldPermissionSync,
    canReadFieldSync,
    canWriteFieldSync,
    isFieldHiddenSync,
    isInitialized,
    isFieldLoading,
    clearCache,
    refreshCache,
    preloadPermissions,
    user?.roles,
    permissionCache,
    enableCaching,
  ]);
};